import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Badge,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import {
  IconAlertTriangle,
  IconBuildingWarehouse,
  IconReceiptTax,
  IconShoppingCart,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useAuth } from '@/app/providers/AuthProvider';
import { PageHeader } from '@/shared/components/PageHeader';
import { fetchUnderStock } from '@/features/cassa/api';

export const Route = createFileRoute('/_app/')({ component: HomePage });

interface QuickAction {
  title: string;
  description: string;
  icon: typeof IconShoppingCart;
  color: string;
  onClick: () => void;
  badge?: number;
}

function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const greetingName = (user?.firstName ?? (user?.email?.split('@')[0] ?? 'utente').split('.')[0]) || 'utente';

  // Sottoscorta live per il badge sulla card Magazzino.
  const underStockQ = useQuery({ queryKey: ['cassa-under-stock'], queryFn: fetchUnderStock, staleTime: 60_000 });
  const underStockCount = underStockQ.data?.count ?? 0;

  const actions: QuickAction[] = [
    {
      title: 'Nuova vendita',
      description: 'Apri la cassa: scan EAN, carrello, scontrino · fattura · DDT.',
      icon: IconShoppingCart,
      color: 'blue',
      onClick: () => void navigate({ to: '/cassa' }),
    },
    {
      title: 'Magazzino',
      description: 'Carico merce, giacenze e articoli sottoscorta.',
      icon: IconBuildingWarehouse,
      color: 'teal',
      onClick: () => void navigate({ to: '/cassa', search: { panel: 'magazzino' } }),
      badge: underStockCount,
    },
    {
      title: 'Fiscale & Sistema TS',
      description: 'Registratore telematico, lotteria scontrini, invio Sistema TS.',
      icon: IconReceiptTax,
      color: 'grape',
      onClick: () => void navigate({ to: '/cassa', search: { panel: 'fiscale' } }),
    },
  ];

  return (
    <Stack gap="lg">
      <PageHeader
        title={`Ciao ${greetingName}`}
        description={
          <Text span size="sm" c="dimmed">
            {dayjs().format('dddd D MMMM YYYY')} · Modulo Cassa
          </Text>
        }
      />

      <Stack gap="xs">
        <Title order={4}>Accesso rapido</Title>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          {actions.map((a) => (
            <UnstyledButton
              key={a.title}
              onClick={a.onClick}
              aria-label={a.title}
            >
              <Card withBorder radius="md" padding="lg" h="100%" style={{ transition: 'box-shadow .15s, transform .15s' }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--mantine-shadow-md)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.transform = ''; }}
              >
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <ThemeIcon size={46} radius="md" variant="light" color={a.color}>
                    <a.icon size={26} />
                  </ThemeIcon>
                  {a.badge && a.badge > 0 ? (
                    <Badge color="red" variant="filled" leftSection={<IconAlertTriangle size={12} />}>
                      {a.badge} sottoscorta
                    </Badge>
                  ) : null}
                </Group>
                <Text fw={600} mt="md">{a.title}</Text>
                <Text size="sm" c="dimmed" mt={4}>{a.description}</Text>
              </Card>
            </UnstyledButton>
          ))}
        </SimpleGrid>
      </Stack>
    </Stack>
  );
}
